import { api, RouterInput, RouterOutput } from "../../services/rpc/RpcProvider";
import { useToast } from "@introist/react-foundation/v2";
import { useCallback } from "react";

// TODO: drop these `display` intersections once @introist/introist-api-types is republished
// with the field (introist-api's forms/v2 FormField schema already has it on the branch that
// adds select field display variants).
export type FormFieldDisplay = "dropdown" | "radio";

export type FormField = RouterOutput["forms"]["v2"]["fields"]["list"][0] & {
  display?: FormFieldDisplay | null;
};
export type FormFieldType = RouterOutput["forms"]["v2"]["fields"]["list"][0]["type"];

export type FormFieldCreate = RouterInput["forms"]["v2"]["fields"]["create"] & {
  display?: FormFieldDisplay | null;
};

type RawFormFieldUpdate = RouterInput["forms"]["v2"]["fields"]["update"];
export type FormFieldUpdate = Omit<RawFormFieldUpdate, "update"> & {
  update: RawFormFieldUpdate["update"] & { display?: FormFieldDisplay | null };
};

export const FormFieldOptions: { key: FormFieldType; title: string }[] = [
  {
    key: "text",
    title: "Text"
  },
  {
    key: "longtext",
    title: "Long text"
  },
  {
    key: "email",
    title: "Email"
  },
  {
    key: "date",
    title: "Date"
  },
  {
    key: "number",
    title: "Number"
  },
  {
    key: "select",
    title: "Select"
  },
  {
    key: "scale",
    title: "Scale"
  },
  { key: "file", title: "File" }
];

export const useFormFields = (formId: string, options?: any) => {
  const { data: formFields } = api.forms.v2.fields.list.useQuery({ formId }, options);

  return { formFields };
};

export const useFormField = (id: string, options?: any) => {
  const { data: field, isFetching } = api.forms.v2.fields.find.useQuery({ id }, options);

  return { field, loading: isFetching };
};

export const useFormFieldActions = () => {
  const toast = useToast();
  const apiUtils = api.useContext();

  const listOp = apiUtils.forms.v2.fields.list;
  const findOp = apiUtils.forms.v2.fields.find;

  const createOp = api.forms.v2.fields.create.useMutation();
  const updateOp = api.forms.v2.fields.update.useMutation();
  const archiveOp = api.forms.v2.fields.archive.useMutation();
  const reorderOp = api.forms.v2.fields.reorder.useMutation();

  const create = useCallback(
    async (req: FormFieldCreate) => {
      return createOp
        .mutateAsync(req)
        .then(async created => {
          await listOp.refetch({ formId: req.formId });
          toast.success("Field created");
          return created;
        })
        .catch(e => {
          toast.error("Failed to create Form Field");
          throw e;
        });
    },
    [createOp, listOp, toast]
  );

  const update = useCallback(
    async (req: FormFieldUpdate) => {
      return updateOp
        .mutateAsync(req)
        .then(async updated => {
          await listOp.refetch();
          await findOp.refetch({ id: req.id });
          toast.success("Field updated");
          return updated;
        })
        .catch(e => {
          toast.error("Failed to update Form Field");
          throw e;
        });
    },
    [updateOp, listOp, findOp, toast]
  );

  const archive = useCallback(
    async (id: string) => {
      await archiveOp
        .mutateAsync({ id })
        .then(async () => {
          await listOp.refetch();
          toast.success("Field archived");
        })
        .catch(() => {
          toast.error("Failed to archive Field");
        });
    },
    [archiveOp, toast, listOp]
  );

  const reorder = useCallback(
    async (formId: string, ordering: string[]) => {
      await reorderOp
        .mutateAsync({ formId, ordering })
        .then(async () => {
          await listOp.refetch({ formId });
        })
        .catch(() => {
          toast.error("Failed to save Field ordering");
        });
    },
    [reorderOp, toast, listOp]
  );

  return { create, update, archive, reorder };
};

export const useFormFieldResolver = () => {
  const { data: formFields } = api.forms.v2.fields.list.useQuery({});

  const resolveTitle = (fieldId: string, includeFormTitle?: boolean) => {
    const field = (formFields ?? []).find(f => f.id === fieldId);
    if (!field) return "Unknown Form Field";

    return `${includeFormTitle ? `${field.formTitle} -  ` : ""} ${field.shortTitle ?? field.title}`;
  };

  return { resolveTitle };
};
